// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { GetBillTosApiParameter, GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import Modal from "@insite/mobius/Modal";
import { ModalPresentationProps } from "@plumberssupply/MobiusOverrides/Modal/Modal";
import ChangeCustomerFulfillmentMethodSelector from "@plumberssupply/Overrides/Widgets/ChangeCustomer/ChangeCustomerFulfillmentMethodSelector";
import ChangeCustomerSelectCustomerContainer, {
    ChangeCustomerSelectCustomerContainerStyles,
} from "@plumberssupply/Overrides/Widgets/ChangeCustomer/ChangeCustomerSelectCustomerContainer";
import { color } from "@plumberssupply/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface FulfillmentMethodChangeModalProps {
    isOpen: any;
    onFulfillmentMethodClose: Function;
}

type Props = FulfillmentMethodChangeModalProps;

interface FulfillmentMethodChangeModalStyles {
    container?: ChangeCustomerSelectCustomerContainerStyles;
    modal?: ModalPresentationProps;
}

const fulfillmentMethodChangeModalStyles: FulfillmentMethodChangeModalStyles = {
    container: {
        container: {
            css: css`
                margin-top: 25px;
                padding-top: 20px;
                border-top: 1px solid ${color("common.ui7")};
            `,
        },
        fulfillmentMethodSelectorGridItem: {
            css: css`
                display: none;
            `,
        },
        buttonsGridItem: {
            css: css`
                justify-content: space-between;
                padding-top: 9px;
            `,
        },
        shipToSelectorGridItem: {
            width: 12,
            css: css`
                div[class^="FormInputWrapper"] {
                    &&&& {
                        margin-top: 6px;
                    }
                }
            `,
        },
    },
    modal: {
        cssOverrides: {
            modalContainer: css`
                &.shipto div[data-test-selector="modal_ModalContent"] {
                    overflow: visible;
                }
            `,
        },
    },
};

const styles = fulfillmentMethodChangeModalStyles;

const FulfillmentMethodChangeModal: FC<Props> = ({ isOpen, onFulfillmentMethodClose }) => {
    const { session } = useAppSelector(state => ({
        session: state.context.session,
    }));

    const [pickUpWarehouseUpdated, setPickUpWarehouseUpdated] = useState(false);
    const [billTosParameter, setBillTosParameter] = useState<GetBillTosApiParameter>({
        page: 1,
        pageSize: 20,
        expand: ["shipTos"],
    });
    const [shipTosParameter, setShipTosParameter] = useState<GetShipTosApiParameter>({
        page: 1,
        pageSize: 20,
        expand: ["validation", "excludeShowAll", "excludeOneTime"],
    });

    const handleModalClose = () => {
        if (pickUpWarehouseUpdated) {
            window.location.reload();
        } else {
            onFulfillmentMethodClose();
        }
    };

    return (
        <Modal
            headline={translate("Update Fulfillment Method")}
            isOpen={isOpen}
            handleClose={handleModalClose}
            data-test-selector="fulfillmentMethodModal"
            size={session.fulfillmentMethod === FulfillmentMethod.PickUp ? 1200 : 500}
            {...styles.modal}
            className={session.fulfillmentMethod === FulfillmentMethod.PickUp ? "pickup" : "shipto"}
        >
            <ChangeCustomerFulfillmentMethodSelector
                displayContentInsideModal={false}
                setPickUpWarehouseUpdated={setPickUpWarehouseUpdated}
            />
            {session.fulfillmentMethod === FulfillmentMethod.Ship && (
                <ChangeCustomerSelectCustomerContainer
                    extendedStyles={styles.container}
                    billTosParameter={billTosParameter}
                    setBillTosParameter={setBillTosParameter}
                    shipTosParameter={shipTosParameter}
                    setShipTosParameter={setShipTosParameter}
                    handleCancelInsideModal={() => onFulfillmentMethodClose()}
                    handleSaveInsideModal={true}
                    preSelectBillTo={true}
                    execludeCreateNewAddress={true}
                    shipToDropDownLabel={`${translate("Select Ship-To")}`}
                />
            )}
        </Modal>
    );
};

export default FulfillmentMethodChangeModal;
